import { useParams } from "react-router";
import { Toast } from "@sscs/ui";
import { useDialog, useReports } from "Hooks";
import {
  useReprocessVersionMutation,
  useUpdateVersionMutation,
} from "Services";
import { Version } from "Types";
import { apiErrorToast } from "Utils";
import {
  useDeleteVersionMutation,
  useReprocessPackageMutation,
} from "../../services/projects.api";

const useVersionActions = (version: Version) => {
  const deleteDialog = useDialog();
  const { groupId } = useParams();
  const { closeAllReports } = useReports();
  const [reanalyzeVersionMutation] = useReprocessVersionMutation();
  const [reanalyzePackageMutation] = useReprocessPackageMutation();
  const [deleteVersionMutation] = useDeleteVersionMutation();
  const [updateVersionMutation] = useUpdateVersionMutation();

  const reanalyzeVersion = async (notifyOnCompletion: boolean) => {
    if (version.artifacts_summary.version.current_status.code === "ERROR") {
      try {
        await reanalyzeVersionMutation({
          versionId: version.version_id,
          notify_on_completion: notifyOnCompletion,
        }).unwrap();
        Toast.show({
          title: "Version sent for reanalysis",
          type: "success",
        });
      } catch (error) {
        apiErrorToast(error, "Could not reanalyze version");
      }
    } else {
      try {
        await reanalyzePackageMutation({
          notify_on_completion: notifyOnCompletion,
          packageId: version.package_id,
        }).unwrap();
        Toast.show({
          title: "Package sent for reanalysis",
          type: "success",
        });
      } catch (error) {
        apiErrorToast(error, "Could not reanalyze package");
      }
    }
  };

  const deleteVersion = async () => {
    try {
      await deleteVersionMutation({
        versionId: version.version_id,
      }).unwrap();
      Toast.show({ title: "Version deleted", type: "success" });
    } catch (error) {
      apiErrorToast(error, "Could not delete version");
      deleteDialog.close();
      return;
    }

    closeAllReports({
      groupId,
      projectName: version.project_name,
      packageName: version.package_name,
      version: version.software_info.version,
    });
  };

  const release = async () => {
    try {
      await updateVersionMutation({
        versionId: version.version_id,
        body: {
          is_released: true,
          software_info: {
            release_date: version.software_info.release_date,
          },
        },
      }).unwrap();
      Toast.show({
        title: `Version marked as released`,
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not mark version as released");
    }
  };

  const revertRelease = async () => {
    try {
      await updateVersionMutation({
        versionId: version.version_id,
        body: {
          is_released: false,
          software_info: {
            release_date: null,
          },
        },
      }).unwrap();
      Toast.show({
        title: `Version marked as unreleased`,
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not mark version as unreleased");
    }
  };

  return {
    reanalyzeVersion,
    deleteVersion,
    release,
    revertRelease,
    deleteDialog,
  };
};

export default useVersionActions;
